import React, {PureComponent} from "react"
import GoogleMapReact from "google-map-react"
import PropTypes from "prop-types"
import {omit, isEqual} from "lodash"

import {MAPS_API_KEY} from "@irati/geolocation/src/constants/googleMaps"
import {calculateDistanceBetweenLatLngs} from "@irati/geolocation/src/helpers/geometry"
import {adjustPrecision} from "@irati/geolocation/src/helpers/latLng"
import {injectIntl} from "@irati/utils/lang"

import DefaultCurrentPositionMarker from "./CurrentPositionMarker"
import DefaultLocationMarker from "./LocationMarker"
import DefaultClusterMarker from "./ClusterMarker"
import {generateClusterData} from "../../helpers/clustering"
import {MODES, BROWSER_POSITION_WITH_FALLBACK} from "../../constants/currentLocationButton"
import {Container, StyledCurrentLocationButton} from "./styled/GeoMap"

const DEFAULT_SETTINGS = {
  zoom: 15,
  dataChangedDistanceThreshold: 400,
  minClusterZoom: 2,
  maxClusterZoom: 14,
  clusterRadius: 40,
  enableCurrentLocationButton: true,
  currentLocationButtonPositionMode: BROWSER_POSITION_WITH_FALLBACK
}

class GeoMap extends PureComponent {
  static propTypes = {
    centerLatLng: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired
    }).isRequired,
    userLatLng: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired
    }),
    className: PropTypes.string,
    onDataChanged: PropTypes.func,
    onClick: PropTypes.func,
    CurrentPositionMarker: PropTypes.func,
    LocationMarker: PropTypes.func,
    ClusterMarker: PropTypes.func,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired
      }).isRequired
    ),
    googleMapsOptions: PropTypes.object,
    settings: PropTypes.shape({
      zoom: PropTypes.number,
      dataChangedDistanceThreshold: PropTypes.number,
      minClusterZoom: PropTypes.number,
      maxClusterZoom: PropTypes.number,
      clusterRadius: PropTypes.number,
      enableCurrentLocationButton: PropTypes.bool,
      currentLocationButtonPositionMode: PropTypes.oneOf(MODES)
    }),
    onClusterZoomHashTableData: PropTypes.func,
    fitBounds: PropTypes.bool
  }

  static defaultProps = {
    className: null,
    onDataChanged: () => {},
    onClick: () => {},
    CurrentPositionMarker: DefaultCurrentPositionMarker,
    LocationMarker: DefaultLocationMarker,
    ClusterMarker: DefaultClusterMarker,
    data: [],
    googleMapsOptions: null,
    userLatLng: null,
    settings: null,
    onClusterZoomHashTableData: () => {},
    fitBounds: false
  }

  state = {
    clusters: [],
    clusterZoomHash: null,
    zoom: null,
    mapBounds: null,
    map: null,
    maps: null,
    fittedBounds: null,
    data: null
  }

  componentDidUpdate() {
    /** Disabling lint because it is nested in a conditional */
    /* eslint-disable react/no-did-update-set-state */
    const {data} = this.props
    const {zoom, mapBounds} = this.state
    const isMapReady = zoom && mapBounds
    if (!isMapReady || !data || this.checkFitBoundsData()) { return }
    this.checkClusterData()
    this.checkZoomData()
    /* eslint-enable react/no-did-update-set-state */
  }

  componentWillUnmount() {
    this.unbindResizeListener()
  }

  checkZoomData = () => {
    const zoomProp = this.props.settings.zoom
    if (zoomProp && (zoomProp !== this.state.zoom)) {
      this.setState({zoom: zoomProp})
    }
  }

  checkFitBoundsData = () => {
    const {data, fitBounds} = this.props
    const {data: stateData} = this.state
    if (fitBounds && !isEqual(data, stateData)) {
      this.setState({data})
      this.fitMapToBounds()
      return true
    }
    return false
  }

  checkClusterData = () => {
    const {data, onClusterZoomHashTableData} = this.props
    const {zoom, mapBounds} = this.state
    const {minClusterZoom, maxClusterZoom, clusterRadius} = this.getSettings()
    const {clusters, zoomHash} = generateClusterData(
      data,
      zoom,
      mapBounds,
      minClusterZoom,
      maxClusterZoom,
      clusterRadius
    )
    if (!isEqual(clusters, this.state.clusters)) {
      this.setState({
        clusters,
        clusterZoomHash: zoomHash
      })
      onClusterZoomHashTableData(zoomHash)
    }
  }

  getSettings = () => {
    return Object.assign({}, DEFAULT_SETTINGS, this.props.settings)
  }

  generateMarkers = () => {
    const {LocationMarker, ClusterMarker} = this.props
    return this.state.clusters.map((cluster) => {
      const {geometry, properties} = cluster
      const [lng, lat] = geometry.coordinates
      const {cluster_id, point_count} = properties
      if (!cluster_id) {
        const markerProps = omit(properties, ["lat", "lng"])
        /*
          markerProps spreads any additional props for custom functionality
        */
        return (
          <LocationMarker
            {...markerProps}
            lat={lat}
            lng={lng} />
        )
      }
      return (
        <ClusterMarker
          key={cluster_id}
          lat={lat}
          lng={lng}>{point_count}</ClusterMarker>
      )
    })
  }

  renderCurrentPositionMarker = () => {
    const {CurrentPositionMarker, userLatLng} = this.props
    if (userLatLng) {
      const {lat, lng} = userLatLng
      return <CurrentPositionMarker lat={lat} lng={lng} />
    }
    return null
  }

  handleMapChange = (data) => {
    const {zoom: newMapZoom, center: newMapCenter, bounds: newMapBounds} = data
    const {mapBounds} = this.state
    const {onDataChanged} = this.props
    const {dataChangedDistanceThreshold} = this.getSettings()
    const currentNeLatLng = newMapBounds ? newMapBounds.ne : null
    const prevNeLatLng = mapBounds ? mapBounds.ne : null

    this.setState({
      zoom: newMapZoom
    })

    let isDistanceThresholdMet = false
    if (currentNeLatLng && prevNeLatLng) {
      const distanceMoved = calculateDistanceBetweenLatLngs(currentNeLatLng, prevNeLatLng)
      isDistanceThresholdMet = distanceMoved >= dataChangedDistanceThreshold
    }
    if (isDistanceThresholdMet || !prevNeLatLng) {
      this.setState({
        mapBounds: newMapBounds
      })
      return onDataChanged(adjustPrecision(newMapCenter), newMapBounds, newMapZoom)
    }
  }

  handleMapClick = (clickEvent) => {
    this.props.onClick(clickEvent)
  }

  handleCurrentLocationButtonClick = (latLng) => {
    this.props.onDataChanged(latLng)
  }

  handleGoogleApiLoaded = ({map, maps}) => {
    const {fitBounds} = this.props
    if (fitBounds && map) {
      this.setState({map, maps})
      this.bindResizeListener()
      this.fitMapToBounds()
    }
  }

  fitMapToBounds = () => {
    const {map, maps, fittedBounds} = this.state
    if (maps && map) {
      const bounds = new maps.LatLngBounds()
      this.props.data.map(({lat, lng}) => bounds.extend(new maps.LatLng(lat, lng)))
      if (!isEqual(fittedBounds, bounds)) {
        this.setState({fittedBounds: bounds})
        map.fitBounds(bounds)
      }
    }
  }

  bindResizeListener = () => {
    const {map, maps} = this.state
    if (maps && map) {
      maps.event.addDomListenerOnce(map, "idle", () => {
        maps.event.addDomListener(window, "resize", () => {
          const {fittedBounds} = this.state
          if (fittedBounds) { map.fitBounds(fittedBounds) }
        })
      })
    }
  }

  unbindResizeListener = () => {
    const {map, maps} = this.state
    if (maps) {
      maps.event.clearListeners(map, "idle")
      maps.event.clearListeners(window, "resize")
    }
  }

  render() {
    const {centerLatLng, className, googleMapsOptions} = this.props
    const {enableCurrentLocationButton, currentLocationButtonPositionMode} = this.getSettings()
    const zoom = this.state.zoom || this.getSettings().zoom

    return (
      <Container className={className}>
        <GoogleMapReact
          yesIWantToUseGoogleMapApiInternals
          bootstrapURLKeys={{key: MAPS_API_KEY}}
          center={centerLatLng}
          zoom={zoom}
          onChange={this.handleMapChange}
          onClick={this.handleMapClick}
          options={googleMapsOptions}
          onGoogleApiLoaded={this.handleGoogleApiLoaded}>
          {this.renderCurrentPositionMarker()}
          {this.generateMarkers()}
        </GoogleMapReact>
        {enableCurrentLocationButton &&
          <StyledCurrentLocationButton
            onClick={this.handleCurrentLocationButtonClick}
            positionMode={currentLocationButtonPositionMode} />
        }
      </Container>
    )
  }
}

export default injectIntl(GeoMap)
